import { type FC, useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { F0Box, F0Button, F0Icon, F0Text } from '@factorialco/f0-react'
import { GetLocationsDocument, type GetLocationsQuery } from 'generated/resources/graphql'

import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  Marketplace,
} from '@factorialco/f0-react/icons/app'
import { useQuery } from 'modules/core/hooks/useQuery'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import formatCurrency from 'modules/core/lib/formatCurrency'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'

import { useItManagementFeatureAvailability } from 'modules/it_management/hooks/useItManagementFeatureAvailability'
import i18n from 'modules/core/lib/i18n'
import { useDeviceCatalogCartContext } from 'modules/it_management/contexts/DeviceCatalogCartContext'
import { useDeviceCatalogCheckout } from 'modules/it_management/hooks/device_catalog/useDeviceCatalogCheckout'
import { navigation } from 'modules/it_management/lib/deviceCatalogNavigation'

import { CartItemRow } from 'modules/it_management/components/device_catalog/MarketplaceCartSidepanel/CartItemRow'

const STEP_IDS = [
  'cart_items',
  'device_configuration',
  'employee_assignment',
  'device_provisioning',
  'shipping_configuration',
  'payment_routing',
  'order_summary',
] as const

type StepId = (typeof STEP_IDS)[number]

const SHOULD_HIDE_TEMP_ORDER_PLAN_STEPS = true
const TEMP_HIDDEN_STEP_IDS: ReadonlySet<StepId> = new Set([
  'device_configuration',
  'employee_assignment',
  'device_provisioning',
  'payment_routing',
])

const VISIBLE_STEP_IDS = SHOULD_HIDE_TEMP_ORDER_PLAN_STEPS
  ? STEP_IDS.filter((stepId) => !TEMP_HIDDEN_STEP_IDS.has(stepId))
  : STEP_IDS

const TOTAL_STEPS = VISIBLE_STEP_IDS.length

type ShippingLocation = GetLocationsQuery['locations']['locations'][number]

const DEFAULT_DELIVERY_ADDRESS = '-'

const toNonEmpty = (...values: (string | null | undefined)[]) =>
  values.find((value) => value?.trim())?.trim()

function formatDeliveryAddress(location: ShippingLocation): string {
  return (
    toNonEmpty(
      [
        location.addressLine1,
        location.addressLine2,
        location.city,
        location.state,
        location.postalCode,
        location.country,
      ]
        .filter((value): value is string => Boolean(value?.trim()))
        .join(', ')
    ) ?? DEFAULT_DELIVERY_ADDRESS
  )
}

function formatShippingSelectionDetail(location: { name: string; address: string }): string {
  return [location.name, location.address].filter((value) => value.trim().length > 0).join(' · ')
}

function formatPrice(amount: number): string {
  return formatCurrency(toCents(amount))
}

const OrderPlanPage: FC = () => {
  const navigate = useNavigate()
  const cart = useDeviceCatalogCartContext()
  const [openSteps, setOpenSteps] = useState<Set<number>>(new Set([0]))
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set())
  const [selectedShippingLocationId, setSelectedShippingLocationId] = useState<string>()

  const orderPlanSubmittedPath = `/device-catalog/${navigation.children.orderPlanSubmitted.path}`

  const { handleCheckout, isCheckingOut } = useDeviceCatalogCheckout(cart, {
    onSuccess: () => navigate(orderPlanSubmittedPath),
  })

  const {
    isItManagementEnabled,
    loading: featureLoading,
    error: featureError,
  } = useItManagementFeatureAvailability()
  const { data: locationsData, loading: locationsLoading } = useQuery(GetLocationsDocument)

  const deviceCount = cart.itemCount
  const totalFormatted = formatPrice(cart.total)
  const shippingLocations = useMemo(
    () =>
      [...(locationsData?.locations.locations ?? [])].sort((left, right) => {
        if (left.main === right.main) return 0
        return left.main ? -1 : 1
      }),
    [locationsData]
  )
  const shippingLocationOptions = useMemo(
    () =>
      shippingLocations.map((location) => ({
        id: location.id,
        name: location.name,
        address: formatDeliveryAddress(location),
      })),
    [shippingLocations]
  )
  const selectedShippingLocation = useMemo(
    () =>
      shippingLocationOptions.find((location) => location.id === selectedShippingLocationId) ??
      shippingLocationOptions[0],
    [selectedShippingLocationId, shippingLocationOptions]
  )
  const shippingStepDetail =
    selectedShippingLocation == null
      ? i18n.t('it_management.device_catalog.order_plan.steps.shipping_configuration_detail')
      : formatShippingSelectionDetail(selectedShippingLocation)
  const isShippingSelectionUnavailable = !locationsLoading && selectedShippingLocation == null

  const stepLabels = useMemo(
    () => ({
      cart_items: {
        title: i18n.t('it_management.device_catalog.order_plan.steps.cart_items'),
        itemsBadge: i18n.t('it_management.device_catalog.order_plan.steps.cart_items_badge', {
          count: deviceCount,
        }),
        detail: i18n.t('it_management.device_catalog.order_plan.steps.cart_items_detail', {
          count: deviceCount,
        }),
        summary: i18n.t('it_management.device_catalog.order_plan.steps.cart_items_summary', {
          count: deviceCount,
          total: totalFormatted,
        }),
      },
      device_configuration: {
        title: i18n.t('it_management.device_catalog.order_plan.steps.device_configuration'),
        detail: i18n.t(
          'it_management.device_catalog.order_plan.steps.device_configuration_description'
        ),
      },
      employee_assignment: {
        title: i18n.t('it_management.device_catalog.order_plan.steps.employee_assignment'),
        detail: i18n.t(
          'it_management.device_catalog.order_plan.steps.employee_assignment_description'
        ),
      },
      device_provisioning: {
        title: i18n.t('it_management.device_catalog.order_plan.steps.device_provisioning'),
        detail: i18n.t(
          'it_management.device_catalog.order_plan.steps.device_provisioning_description'
        ),
      },
      shipping_configuration: {
        title: i18n.t('it_management.device_catalog.order_plan.steps.shipping_configuration'),
        detail: shippingStepDetail,
      },
      payment_routing: {
        title: i18n.t('it_management.device_catalog.order_plan.steps.payment_routing'),
        detail: i18n.t('it_management.device_catalog.order_plan.steps.payment_routing_detail'),
      },
      order_summary: {
        title: i18n.t('it_management.device_catalog.order_plan.steps.order_summary'),
        detail: i18n.t('it_management.device_catalog.order_plan.steps.order_summary_detail', {
          total: totalFormatted,
        }),
      },
    }),
    [deviceCount, shippingStepDetail, totalFormatted]
  )

  const handleContinue = useCallback((stepIndex: number) => {
    setCompletedSteps((prev) => new Set([...prev, stepIndex]))
    setOpenSteps((prev) => {
      const next = new Set(prev)
      next.delete(stepIndex)
      if (stepIndex < TOTAL_STEPS - 1) {
        next.add(stepIndex + 1)
      }
      return next
    })
  }, [])

  const handlePlaceOrder = useCallback(() => {
    handleCheckout(selectedShippingLocation?.address)
  }, [handleCheckout, selectedShippingLocation])

  const handleStepClick = useCallback((index: number) => {
    setOpenSteps((prev) => {
      const next = new Set(prev)
      if (next.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
  }, [])

  const handleShippingLocationChange = useCallback((locationId: string) => {
    setSelectedShippingLocationId(locationId)
  }, [])

  if (cart.items.length === 0) {
    navigate('/device-catalog', { replace: true })
    return null
  }

  return (
    <ResponsivePage.Body
      authorized={isItManagementEnabled}
      error={featureError}
      loading={featureLoading}
    >
      <div style={{ maxWidth: 830 }}>
        <F0Box display="flex" flexDirection="column" padding="xl" gap="lg">
          <div style={{ alignSelf: 'start', width: 150 }}>
            <F0Button
              variant="ghost"
              label={i18n.t('it_management.device_catalog.order_plan.back_to_catalog')}
              icon={ChevronLeft}
              iconPosition="start"
              onClick={() => navigate('/device-catalog')}
            />
          </div>
          <F0Box display="flex" flexDirection="column" gap="xs">
            <F0Text
              variant="label"
              content={i18n.t('it_management.device_catalog.order_plan.title')}
            />
            <F0Text
              variant="description"
              content={i18n.t('it_management.device_catalog.order_plan.subtitle', {
                count: deviceCount,
              })}
            />
          </F0Box>

          <F0Box display="flex" flexDirection="column" gap="md">
            {VISIBLE_STEP_IDS.map((stepId, index) => {
              const isOpen = openSteps.has(index)
              const isDone = completedSteps.has(index)
              const isLast = index === TOTAL_STEPS - 1
              const labels = stepLabels[stepId]
              const isCartStep = stepId === 'cart_items'
              const detail =
                labels == null
                  ? undefined
                  : stepId === 'order_summary'
                    ? labels.detail
                    : isCartStep || stepId === 'device_provisioning'
                      ? undefined
                      : labels.detail

              return (
                <F0Box
                  key={stepId}
                  border="default"
                  borderColor="secondary"
                  borderRadius="md"
                  overflow="hidden"
                >
                  <button
                    type="button"
                    onClick={() => handleStepClick(index)}
                    className="flex w-full flex-row items-center gap-3 px-4 py-4 text-left hover:bg-f1-bg-secondary/50"
                    aria-expanded={isOpen}
                    aria-controls={`order-plan-step-${index}`}
                    id={`order-plan-step-heading-${index}`}
                  >
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center" aria-hidden>
                      {isDone ? (
                        <F0Icon icon={Check} size="md" color="default" />
                      ) : (
                        <span className="text-sm font-medium text-f1-text-bold">{index + 1}</span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1 flex flex-col gap-0.5">
                      <div className="flex flex-row items-center gap-2 flex-wrap">
                        <F0Text variant="label" content={labels?.title ?? ''} />
                        {labels != null && isCartStep && 'itemsBadge' in labels && (
                          <span className="rounded-full bg-f1-bg-secondary px-2 py-0.5 text-xs text-f1-text-secondary">
                            {labels.itemsBadge}
                          </span>
                        )}
                      </div>
                      {detail != null && <F0Text variant="description" content={detail} />}
                    </div>
                    <span className="flex shrink-0" aria-hidden>
                      <F0Icon icon={isOpen ? ChevronUp : ChevronDown} size="sm" color="default" />
                    </span>
                  </button>

                  {isOpen && (
                    <F0Box
                      id={`order-plan-step-${index}`}
                      role="region"
                      aria-labelledby={`order-plan-step-heading-${index}`}
                      padding="lg"
                      borderTop="default"
                      borderColor="secondary"
                      display="flex"
                      flexDirection="column"
                      gap="lg"
                      {...(index > 0 && { 'data-order-plan-step-content': true })}
                    >
                      {stepId === 'cart_items' && (
                        <F0Box display="flex" flexDirection="column" gap="md">
                          {cart.items.map((item) => (
                            <CartItemRow
                              key={`${item.variantId}-${item.offerId}`}
                              item={item}
                              onUpdateQuantity={cart.updateQuantity}
                              onRemove={cart.removeItem}
                              variant="checkout"
                            />
                          ))}
                        </F0Box>
                      )}
                      {stepId === 'device_provisioning' && (
                        <F0Box background="tertiary" borderRadius="md" padding="md">
                          <F0Text
                            variant="description"
                            content={i18n.t(
                              'it_management.device_catalog.order_plan.steps.device_provisioning_no_mdm_message'
                            )}
                          />
                        </F0Box>
                      )}
                      {['device_configuration', 'employee_assignment', 'payment_routing'].includes(
                        stepId
                      ) &&
                        labels != null && (
                          <F0Box background="tertiary" borderRadius="md" padding="md">
                            <F0Text variant="description" content={labels.detail} />
                          </F0Box>
                        )}
                      {stepId === 'shipping_configuration' && labels != null && (
                        <F0Box display="flex" flexDirection="column" gap="md">
                          {shippingLocationOptions.length > 0 ? (
                            <div
                              role="radiogroup"
                              aria-label={labels.title}
                              className={[
                                'flex flex-col gap-3',
                                shippingLocationOptions.length > 3 ? 'overflow-y-auto pr-1' : '',
                              ].join(' ')}
                              style={
                                shippingLocationOptions.length > 3
                                  ? { maxHeight: '16rem' }
                                  : undefined
                              }
                            >
                              {shippingLocationOptions.map((location) => {
                                const isSelected = selectedShippingLocation?.id === location.id

                                return (
                                  <label
                                    key={location.id}
                                    className={[
                                      'flex cursor-pointer items-start gap-3 rounded-md border px-4 py-3 transition-colors',
                                      isSelected
                                        ? 'border-f1-border-bold bg-f1-bg-secondary'
                                        : 'border-f1-border-secondary bg-f1-bg-primary',
                                    ].join(' ')}
                                  >
                                    <input
                                      type="radio"
                                      name="shipping-location"
                                      value={location.id}
                                      checked={isSelected}
                                      onChange={() => handleShippingLocationChange(location.id)}
                                      className="mt-1"
                                    />
                                    <div className="min-w-0 flex-1">
                                      <F0Text variant="label" content={location.name} />
                                      <F0Text variant="description" content={location.address} />
                                    </div>
                                  </label>
                                )
                              })}
                            </div>
                          ) : (
                            <F0Box background="tertiary" borderRadius="md" padding="md">
                              <F0Text
                                variant="description"
                                content={i18n.t(
                                  'it_management.device_catalog.order_plan.steps.shipping_configuration_no_locations'
                                )}
                              />
                            </F0Box>
                          )}
                        </F0Box>
                      )}
                      {stepId === 'order_summary' && (
                        <F0Box background="tertiary" borderRadius="md" padding="md">
                          <F0Box display="flex" flexDirection="column" gap="sm">
                            <F0Box
                              display="flex"
                              flexDirection="row"
                              justifyContent="between"
                              alignItems="center"
                            >
                              <F0Text
                                variant="label"
                                content={i18n.t('it_management.device_catalog.cart.total')}
                              />
                              <F0Text variant="label" content={totalFormatted} />
                            </F0Box>
                          </F0Box>
                        </F0Box>
                      )}

                      <F0Box data-order-plan-step-actions data-it-management-primary-action>
                        <F0Box display="flex" justifyContent="end">
                          {isLast ? (
                            <F0Button
                              label={i18n.t('it_management.device_catalog.order_plan.place_order')}
                              variant="default"
                              size="md"
                              icon={Marketplace}
                              onClick={handlePlaceOrder}
                              disabled={isCheckingOut || isShippingSelectionUnavailable}
                            />
                          ) : (
                            <F0Button
                              label={i18n.t(
                                'it_management.device_catalog.order_plan.continue_with_arrow'
                              )}
                              variant="default"
                              size="md"
                              onClick={() => handleContinue(index)}
                              disabled={
                                stepId === 'shipping_configuration' &&
                                isShippingSelectionUnavailable
                              }
                            />
                          )}
                        </F0Box>
                      </F0Box>
                    </F0Box>
                  )}
                </F0Box>
              )
            })}
          </F0Box>
        </F0Box>
      </div>
    </ResponsivePage.Body>
  )
}

export default OrderPlanPage
